﻿(function (angular) {
  "use strict";

  var SELECTED_BODY_PAINT_MINIMUM_FRACTION_MATCH = 0.8;

  angular.module("Toyota.Factories").factory("VehicleService", [
    "$q",
    "$filter",
    "Range",
    "PaintListColourMatcher",
    function ($q, $filter, Range, PaintListColourMatcher) {
      // Managed state of current vehicle and options.
      var vehicle = {
        model: {},
        gradeId: "",
        grade: {},
        variant: {},
        optionPacks: {},
        paint: {},
        trim: {},
        materialCode: "",
        pricingParams: {},
      };

      function setCurrentVehicleByMaterialCode(materialCode) {
        vehicle.materialCode = materialCode;

        var gradePromise = Range.getGradeIdByMaterialCode(materialCode).then(function (gradeId) {
          vehicle.gradeId = gradeId;
          return gradeId;
        });

        var paintPromise = Range.getPaintByMaterialCode(materialCode);

        var variantPromise = Range.getVariantByMaterialCode(materialCode);

        var trimPromise = Range.getTrimByMaterialCode(materialCode);

        return $q.all([gradePromise, paintPromise, variantPromise, trimPromise]).then(function (result) {
          /* eslint-disable no-magic-numbers */
          var gradeId = result[0];
          var paint = result[1];
          var variant = result[2];
          var trim = result[3];
          /* eslint-enable no-magic-numbers */

          vehicle.grade = Range.getGradeByIdFromCache(gradeId);
          vehicle.model = vehicle.grade.vehicleModel;

          vehicle.paint = angular.extend(paint, {
            selectedBodyPaintID: paint.ID,
            selectedBodyPaintName: paint.Name,
            selectedBodyPaintHex: paint.BodyPaintSwatchHex,
          });

          vehicle.variant = variant;

          vehicle.trim = trim;
          vehicle.optionPacks = null;

          vehicle.pricingParams.usageType = paint.UsageType;

          return vehicle;
        });
      }

      function setVehiclePricingParamsPostcode(postcode) {
        vehicle.pricingParams.postcode = postcode;
      }

      function getAdjacentGradesMaterialCodesByMaterialCode(materialCode, filterSourceGrade) {
        var removeSourceGrade = typeof filterSourceGrade !== "undefined" ? filterSourceGrade : false;

        var grades = Range.getGradesList();

        return Range.getGradeIdByMaterialCode(materialCode).then(function (gradeIdResult) {
          var adjacentGrades = grades;
          if (removeSourceGrade) {
            adjacentGrades = grades.filter(function (e) {
              return e.gradeId !== gradeIdResult;
            });
          }

          var adjacentMaterialCodes = adjacentGrades.map(function (grade) {
            return getClosestVariantIdInGrade(grade).then(getMatchedPaintFromVariant);
          });

          return $q.all(adjacentMaterialCodes).then(function (result) {
            return result;
          });
        });
      }

      function getMaterialCodeByAdjacentGradeMaterialCode(materialCode, relativeIndex) {
        var grades = Range.getGradesList();

        return Range.getGradeIdByMaterialCode(materialCode)
          .then(function (result) {
            var sourceGradeIndex = grades
              .map(function (e) {
                return e.gradeId;
              })
              .indexOf(result);
            return sourceGradeIndex;
          })
          .then(function (sourceGradeIndex) {
            var targetIndex = sourceGradeIndex + relativeIndex;
            var targetGrade = grades[targetIndex];

            if (typeof targetGrade === "undefined") {
              return null;
            }

            return targetGrade;
          })
          .then(getClosestVariantIdInGrade)
          .then(getMatchedPaintFromVariant);
      }

      function getClosestVariantIdInGrade(grade) {
        if (grade === null) {
          return null;
        }

        var closestVariantId = calculateClosestVariantId(grade);
        return Range.getVariantById(closestVariantId).then(function (result) {
          return result;
        });
      }

      function getMatchedPaintFromVariant(variant) {
        if (variant === null) {
          return {};
        }

        return matchPaintsByVariant([variant]).then(function (matchedPaint) {
          return matchedPaint;
        });
      }

      function calculateClosestVariantId(grade) {
        if (grade === null) {
          return null;
        }

        var variants = Range.getVariantsByGradeIdFromCache(grade.gradeId);
        var variantMatches = getVariantMatchCounts(variants);
        var highestMatchCount = Math.max.apply(null, variantMatches);

        if (highestMatchCount === 0) {
          return grade.baseVariantId;
        }

        var variantIndex = variantMatches.indexOf(highestMatchCount);

        return variants[variantIndex].id;
      }

      function getVariantMatchCounts(variants) {
        return variants.map(function (variant) {
          var matchCount = 0;

          Object.keys(vehicle.variant).forEach(function (key) {
            if (variant[key] === vehicle.variant[key]) {
              matchCount++;
            }
          });

          return matchCount;
        });
      }

      function matchPaintsByVariant(variantToMatch) {
        vehicle.paint.paints = [];

        var allVariantsWithNamesNormalised = angular.copy(variantToMatch);
        allVariantsWithNamesNormalised = allVariantsWithNamesNormalised.map(function (variant) {
          var variantCopy = angular.copy(variant);
          variantCopy.name = variantCopy.name;
          return variantCopy;
        });

        var variantsMatchingFirstVariantName = allVariantsWithNamesNormalised.filter(function (variant) {
          return variant.name === allVariantsWithNamesNormalised[0].name;
        });

        var variantIds = variantsMatchingFirstVariantName.map(function (variant) {
          return variant.ID;
        });

        return Range.getUniquePaintsByVariantIds(variantIds, vehicle.pricingParams).then(function (paints) {
          var bodyPaints = updateBodyPaints(paints);
          var matchedBodyPaint = getSelectedBodyPaint(bodyPaints);
          var finalMatchedPaint = processNewlySelectedBodyPaint(matchedBodyPaint, bodyPaints);

          return finalMatchedPaint;
        });
      }

      function updateBodyPaints(paints) {
        return Range.getBodyPaintsWithRoofOptionsByPaintIdsFromCache(
          paints.map(function (paint) {
            return paint.ID;
          }),
          null
        );
      }

      function getSelectedBodyPaint(bodyPaints) {
        var closestColourTuple = PaintListColourMatcher({
          colourTuples: bodyPaints.map(function (i) {
            return {
              description: i.BodyPaintDescription,
              hex: i.BodyPaintSwatchHex,
            };
          }),
        }).closestMatch(
          {
            description: vehicle.paint.selectedBodyPaintName,
            hex: vehicle.paint.selectedBodyPaintHex,
          },
          SELECTED_BODY_PAINT_MINIMUM_FRACTION_MATCH
        );

        if (closestColourTuple) {
          return $filter("arrayFind")(bodyPaints, function (currentPaint) {
            return currentPaint.BodyPaintDescription === closestColourTuple.description;
          });
        }

        return bodyPaints[0];
      }

      function processNewlySelectedBodyPaint(matchedBodyPaint, bodyPaints) {
        if (matchedBodyPaint.ID === "") {
          return undefined;
        }

        var bodyPaint = getBodyPaintByBodyPaintID(matchedBodyPaint.BodyPaintId, bodyPaints);
        return updateSelectedRoofPaint(bodyPaint, bodyPaints);
      }

      function updateSelectedRoofPaint(bodyPaint, bodyPaints) {
        var name = getBodyPaintByBodyPaintID(bodyPaint.BodyPaintId, bodyPaints).BodyPaintDescription;
        var item;

        item = $filter("arrayFind")(bodyPaint.RoofOptions, function (currentRoofPaint) {
          return currentRoofPaint.RoofPaintDescription === vehicle.paint.selectedRoofPaintName;
        });

        item =
          item ||
          $filter("arrayFind")(bodyPaint.RoofOptions, function (currentRoofPaint) {
            return currentRoofPaint.RoofPaintDescription === name;
          });

        item = item || bodyPaint.RoofOptions[0];

        if (item) {
          return item;
        }

        return undefined;
      }

      function getBodyPaintByBodyPaintID(id, bodyPaints) {
        return $filter("arrayFind")(bodyPaints, function (currentPaint) {
          return currentPaint.BodyPaintId === id;
        });
      }

      function displayCurrentVehicleConfiguration() {
        return vehicle;
      }

      // Exports
      return {
        setCurrentVehicleByMaterialCode: setCurrentVehicleByMaterialCode,
        setVehiclePricingParamsPostcode: setVehiclePricingParamsPostcode,
        getMaterialCodeByAdjacentGradeMaterialCode: getMaterialCodeByAdjacentGradeMaterialCode,
        getAdjacentGradesMaterialCodesByMaterialCode: getAdjacentGradesMaterialCodesByMaterialCode,
        displayCurrentVehicleConfiguration: displayCurrentVehicleConfiguration,
      };
    },
  ]);
})(window.angular);
